import React from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../editor-plugin-ai';
import { type Recommendation } from '../pm-plugins/ai-proactive/api';
import {
	insertRecommendation,
	removeRecommendation,
	selectRecommendation,
	toggleProactiveAISuggestionDisplay,
} from '../pm-plugins/ai-proactive/commands';
import { getPluginState as getProactivePluginState } from '../pm-plugins/ai-proactive/plugin-factory';
import { getAllRecommendations } from '../pm-plugins/ai-proactive/utils';
import { getPluginState as getSpellingGrammarPluginState } from '../pm-plugins/ai-spelling-grammar/plugin-factory';
import { getPluginState as getDecorationPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';

import { RecommendationPanel } from './recommendation-panel';

export const getContextPanel =
	(getEditorView?: () => EditorView | undefined) =>
	(api: ExtractInjectionAPI<AIPlugin> | undefined) =>
	(state: EditorState) => {
		// FIXME: future implementation of suggestion sidebar
		// get relavent plugin states from proactive to find the list of suggestions to recommend
		// EIther plugin can trigger this sidecar to open, by setting a flag in state.
		// we might also open context panel based on similar trigger with how floating toolbar works, maybe...

		const proactivePluginState = getProactivePluginState(state);
		const spellingGrammarPluginState = getSpellingGrammarPluginState(state);
		if (proactivePluginState) {
			const { isProactiveEnabled, isProactiveContextPanelOpen, selectedRecommendationId } =
				proactivePluginState;
			// FIXME: Decide on the correct trigger for opening/closing the context panel. Currently the context panel
			// will just open as soon as there a recommendations. This is for testing purposes only.
			if (isProactiveEnabled && isProactiveContextPanelOpen) {
				const decorationPluginState = getDecorationPluginState(state);
				if (decorationPluginState.modalDecorationSet === DecorationSet.empty) {
					const recommendations = getAllRecommendations(proactivePluginState);

					if (!recommendations) {
						return null;
					}

					// FIXME: Pass necesary actions to apply/dismiss the recommendations.
					const onRecommendationApply = ({
						recommendation,
					}: {
						recommendation: Recommendation;
					}) => {
						const editorView = getEditorView?.();
						if (!editorView) {
							return null;
						}
						insertRecommendation(
							recommendation.id,
							'contextPanel',
							api?.contextPanel?.actions.applyChange,
						)(editorView.state, editorView.dispatch);
					};

					const onRecommendationDismiss = ({
						recommendation,
					}: {
						recommendation: Recommendation;
					}) => {
						// FIXME: Dismiss the recommendation from the list.
						const editorView = getEditorView?.();
						if (!editorView) {
							return null;
						}
						removeRecommendation(
							recommendation.id,
							'contextPanel',
							api?.contextPanel?.actions.applyChange,
						)(editorView.state, editorView.dispatch);
					};

					const onRecommendationSelect = ({
						recommendation,
					}: {
						recommendation: Recommendation;
					}) => {
						const editorView = getEditorView?.();
						if (!editorView) {
							return;
						}
						const { state, dispatch } = editorView;
						selectRecommendation(recommendation.id, api?.contextPanel?.actions.applyChange)(
							state,
							dispatch,
						);
					};

					const onRecommendationHover = ({
						recommendation,
					}: {
						recommendation: Recommendation;
					}) => {
						// FIXME: Trigger highlight of the recommendation from the list.
					};

					const onPanelClose = () => {
						const editorView = getEditorView?.();
						if (!editorView) {
							return;
						}
						const { state, dispatch } = editorView;
						toggleProactiveAISuggestionDisplay('recommendationPanel')(state, dispatch);
					};

					const onSpellingGrammar = () => {
						// FIXME: Trigger next spelling and grammar suggestion.
					};

					const isSpellingGrammarEnabled =
						spellingGrammarPluginState?.isSpellingAndGrammarEnabled ?? false;
					// FIXME: Get the count of spelling and grammar errors.
					const spellingGrammarErrorCount = 0;

					return (
						<RecommendationPanel
							isSpellingGrammarEnabled={isSpellingGrammarEnabled}
							spellingGrammarErrorCount={spellingGrammarErrorCount}
							recommendations={recommendations}
							selectedRecommendationId={selectedRecommendationId}
							onRecommendationApply={onRecommendationApply}
							onRecommendationDismiss={onRecommendationDismiss}
							onRecommendationSelect={onRecommendationSelect}
							onRecommendationHover={onRecommendationHover}
							onSpellingGrammar={onSpellingGrammar}
							onPanelClose={onPanelClose}
						/>
					);
				}
			}
			return null;
		}
	};
